/* Skeleton.jsx — the app-wide three-state loading kit (2026-07-18 mandate:
 * "never render fake 0 / €0 / empty tables while loading").
 *
 * The contract every data page follows:
 *   1. LOADING  — data not arrived → skeletons that MATCH the final layout
 *                 (page chrome stays visible; no fake values anywhere);
 *   2. LOADED   — real content fades in (<Reveal>, 200ms opacity, no movement
 *                 so there is never a layout shift);
 *   3. EMPTY    — only AFTER the API confirms "no data" do the real zero
 *                 states render ("0", "€0", "No clients yet").
 *
 * Shimmer + fade live in skeleton.css (prefers-reduced-motion respected).
 * The reference in-app usage predating this kit: salescrm/OrderWorkspace.
 */
import React from "react";
import "./skeleton.css";

/** One shimmer block. w/h accept numbers (px) or CSS strings. */
export function Sk({ w = "100%", h = 12, r = 6, className = "", style }) {
  return (
    <span
      className={"lhc-sk " + className}
      aria-hidden="true"
      style={{ width: w, height: h, borderRadius: r, ...style }}
    />
  );
}

/** Stack of skeleton rows (tables / lists / feeds). */
export function SkRows({ n = 4, h = 44, gap = 10, r = 10 }) {
  return (
    <div className="lhc-sk-rows" style={{ gap }} aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <Sk key={i} h={h} r={r} />
      ))}
    </div>
  );
}

/** Big stat-card value placeholder (where the number will land). */
export function SkStat({ w = 64, h = 30 }) {
  return <Sk w={w} h={h} r={8} />;
}

/** Chart placeholder: baseline bars inside the chart's box — reads as "a chart
 *  is coming", never as "revenue is €0". */
export function SkChart({ h = 180 }) {
  const bars = [42, 66, 34, 78, 52, 88, 60, 72, 46, 80, 58, 68];
  return (
    <div className="lhc-sk-chart" style={{ height: h }} aria-hidden="true">
      {bars.map((b, i) => (
        <span key={i} className="lhc-sk lhc-sk-bar" style={{ height: `${b}%` }} />
      ))}
    </div>
  );
}

/** Fade-in wrapper for freshly loaded content (150–250ms band; opacity only). */
export function Reveal({ children, className = "", as: Tag = "div", style }) {
  return (
    <Tag className={"lhc-reveal " + className} style={style}>
      {children}
    </Tag>
  );
}
